import React, { useEffect, useState } from 'react';
import { GameState, Card as CardData } from '../types';
import { Language } from '../data/translations';
import Card from './Card';

interface Props {
    gameState: GameState;
    localPlayerId: number;
    language: Language;
}

const BrokenSecretRevealModal: React.FC<Props> = ({ gameState, localPlayerId, language }) => {
    const { brokenSecretState, players } = gameState;

    if (!brokenSecretState) return null;

    const targetPlayer = players.find(p => p.id === brokenSecretState.targetId);
    const initiatorPlayer = players.find(p => p.id === brokenSecretState.initiatorId);

    if (!targetPlayer || !initiatorPlayer) return null;

    // We are "revealing" the target's hand.
    // We need to show all cards in the target's hand.
    // Highlight the ones that are being stolen.
    const cards = targetPlayer.hand;
    const stolenIds = new Set(brokenSecretState.stolenCardIds);

    const isLocalInitiator = localPlayerId === brokenSecretState.initiatorId;
    const isLocalTarget = localPlayerId === brokenSecretState.targetId;

    // Only show the reveal screen to the initiator (owner of the card)
    if (!isLocalInitiator) {
        return null;
    }

    // Animation state to delay the "fly away" effect
    const [animatingSteal, setAnimatingSteal] = useState(false);

    useEffect(() => {
        // Start "stealing" animation halfway through the duration
        const timer = setTimeout(() => {
            setAnimatingSteal(true);
        }, (brokenSecretState.revealDuration || 3000) * 0.6);
        return () => clearTimeout(timer);
    }, [brokenSecretState.revealDuration]);

    // If I am the target, I see my cards being revealed (maybe with a warning?)
    // If I am the initiator, I see the cards I found.

    const title = isLocalInitiator
        ? "Broken Secret Revealed!"
        : `${initiatorPlayer.name} is inspecting your hand!`;

    return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in">
            <h2 className="text-3xl font-bold text-white mb-8 drop-shadow-md animate-bounce-in">{title}</h2>

            <div className="flex gap-4 justify-center items-center flex-wrap max-w-5xl p-4">
                {cards.map((card) => {
                    const isStolen = stolenIds.has(card.id);

                    // If it's stolen, we animate it flying away (fading out and moving down/up) based on who is viewing
                    // For simplified UI here, we just use a transform or opacity fade.
                    // The actual "particle" animation might be handled by the effect system, but this is the "Static Reveal" UI.

                    return (
                        <div
                            key={card.id}
                            className={`
                            relative transition-all duration-700 ease-in-out
                            ${isStolen && animatingSteal ? 'opacity-0 scale-50 translate-y-20' : 'opacity-100 scale-100'}
                        `}
                        >
                            <div className={`
                             relative p-1 rounded-xl 
                             ${isStolen ? 'ring-4 ring-red-500 shadow-[0_0_20px_rgba(239,68,68,0.6)]' : 'opacity-70 grayscale-[0.3]'}
                         `}>
                                <Card card={card} rotation={0} language={language} />
                                {isStolen && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <span className="text-red-500 font-black text-4xl drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] -rotate-12 border-2 border-red-500 rounded-full px-2 py-1 bg-black/40">STOLEN</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-8 text-xl text-slate-300 font-semibold">
                {stolenIds.size > 0
                    ? `Broken Secret found ${Math.floor(stolenIds.size / 3)} set${Math.floor(stolenIds.size / 3) !== 1 ? 's' : ''} of secrets!`
                    : "No secrets found..."}
            </div>
        </div>
    );
};

export default BrokenSecretRevealModal;
